import { GAME_RESULT_VARIANT, GAME_SESSION_STATUS, MAZE_MAX_LIVES, MAZE_START_TIMER } from '@/constants/maze';
import type { BoardDifficulty } from '@/features/live-engine/board-pool';
import { liveGameEngine } from '@/features/live-engine/live-game-engine';
import type {
  EndGameResponse,
  GameMazeDto,
  GameSessionResponse,
  GameSessionStatusCode,
  SubmitMovesRequest,
} from '@/types/api/game';
import { MazeGamePhase } from '@/types/maze-phase';
import type { GameResultVariant } from '@/types/result-content';
import type { StageId } from '@/types/stage-theme';
import { normalizeStageId } from '@/utils/stage/stage-utils';
import { create, type StateCreator } from 'zustand';

export interface LiveGameResult {
  stage: StageId;
  score: number;
  completed: number;
  total: number;
  variant: GameResultVariant;
}

export interface LiveSessionSlice {
  session: GameSessionResponse | null;
  hasActiveLiveSession: boolean;
  isStarting: boolean;
  isSyncing: boolean;
}

export interface LiveHudSlice {
  phase: MazeGamePhase;
  score: number;
  timer: number;
  level: number;
  lives: number;
}

interface LiveActions {
  setResult: (payload: LiveGameResult) => void;
  clear: () => void;
  setPhase: (phase: MazeGamePhase) => void;
  setScore: (score: number) => void;
  setTimer: (timer: number) => void;
  decrementTimer: () => void;
  startLivePlaying: (roundNumber: number | undefined, timerSeconds: number, score: number) => void;
  loseLife: () => number;
  finishLiveSuccess: (totalScore: number) => void;
  /** Writes terminal result payload from current live session HUD state. */
  publishTerminalResult: (stage: StageId, variant: LiveGameResult['variant']) => void;
  setSession: (session: GameSessionResponse | null) => void;
  clearLiveSession: () => void;
  /** Clears stale WIN/LOSE HUD from a prior visit so /game bootstrap can run. */
  prepareLiveGameRouteEntry: () => void;
  startGame: (difficulty: BoardDifficulty) => Promise<GameSessionResponse>;
  fetchStatus: () => Promise<GameSessionResponse | null>;
  applyStatusOrThrow: (data: GameSessionResponse) => void;
  submitMoves: (body: SubmitMovesRequest) => Promise<void>;
  advanceRoundAfterReachingEnd: () => Promise<{
    nextMaze: GameMazeDto | null;
    endBoard: { gameOver: boolean; roundScore: number };
    endGame: EndGameResponse | null;
  }>;
  skipRound: () => Promise<{
    nextMaze: GameMazeDto | null;
    endBoard: { gameOver: boolean; roundScore: number };
    endGame: EndGameResponse | null;
  }>;
  endGameEarly: (options?: { refreshStatus?: boolean }) => Promise<EndGameResponse | null>;
}

export type LiveStore = LiveSessionSlice &
  LiveHudSlice & {
    result: LiveGameResult | null;
    /** Last `endGame()` API `totalScore`; cleared after terminal result is written or session resets. */
    lastEndGameTotalScore: number | null;
  } & LiveActions;

const sessionInitial: LiveSessionSlice = {
  session: null,
  hasActiveLiveSession: false,
  isStarting: false,
  isSyncing: false,
};

const hudInitial: LiveHudSlice = {
  phase: MazeGamePhase.START,
  score: 0,
  timer: MAZE_START_TIMER,
  level: 1,
  lives: MAZE_MAX_LIVES,
};

function isSessionActive(status: GameSessionStatusCode): boolean {
  return status === GAME_SESSION_STATUS.STARTED;
}

function resolveRoundsProgress(data: {
  status: number;
  totalRounds: number;
  completedBoards?: number;
}): { total: number; completed: number } {
  const total = Math.max(1, data.totalRounds);
  if (typeof data.completedBoards === 'number') {
    return {
      total,
      completed: Math.min(Math.max(0, data.completedBoards), total),
    };
  }
  if (data.status === GAME_SESSION_STATUS.ENDED) {
    return { total, completed: total };
  }
  return { total, completed: 0 };
}

const liveStoreSlice: StateCreator<LiveStore> = (set, get) => ({
  ...sessionInitial,
  ...hudInitial,
  result: null,
  lastEndGameTotalScore: null,

  setResult: (payload) => set({ result: payload }),
  clear: () => set({ result: null, lastEndGameTotalScore: null }),

  setPhase: (phase) => set({ phase }),
  setScore: (score) => set({ score }),
  setTimer: (timer) => set({ timer }),
  decrementTimer: () => set((state) => ({ timer: Math.max(0, state.timer - 1) })),
  startLivePlaying: (roundNumber, timerSeconds, score) => {
    set({
      phase: MazeGamePhase.PLAYING,
      level: Math.max(1, roundNumber ?? hudInitial.level),
      timer: Math.max(0, timerSeconds),
      score,
      lives: MAZE_MAX_LIVES,
    });
  },
  loseLife: () => {
    const lives = Math.max(0, get().lives - 1);
    set({ lives });
    return lives;
  },
  finishLiveSuccess: (totalScore) =>
    set((state) => ({
      phase: MazeGamePhase.WIN,
      score: totalScore,
      lastEndGameTotalScore: totalScore,
      session: state.session
        ? {
            ...state.session,
            scoreboard: {
              ...state.session.scoreboard,
              totalScore,
            },
          }
        : state.session,
    })),

  publishTerminalResult: (stage, variant) => {
    const state = get();
    const stageId = normalizeStageId(stage);
    const rounds =
      state.session === null
        ? { total: 1, completed: variant === GAME_RESULT_VARIANT.SUCCESS ? 1 : 0 }
        : resolveRoundsProgress({
            status: state.session.status,
            totalRounds: state.session.totalRounds,
            completedBoards: state.session.completedBoards,
          });
    const completedGames = Math.min(rounds.completed, rounds.total);
    const boardTotal = state.session?.scoreboard.totalScore;
    const lastApi = state.lastEndGameTotalScore;
    let resolvedScore = state.score;
    if (typeof lastApi === 'number' && Number.isFinite(lastApi)) {
      resolvedScore = lastApi;
    } else if (typeof boardTotal === 'number' && Number.isFinite(boardTotal)) {
      resolvedScore = boardTotal;
    }
    const resolvedVariant =
      resolvedScore === 0 ? GAME_RESULT_VARIANT.FAILURE : GAME_RESULT_VARIANT.SUCCESS;
    set({
      result: {
        stage: stageId,
        score: resolvedScore,
        completed: completedGames,
        total: rounds.total,
        variant: resolvedVariant,
      },
      phase: resolvedVariant === GAME_RESULT_VARIANT.SUCCESS ? MazeGamePhase.WIN : MazeGamePhase.LOSE,
      lastEndGameTotalScore: null,
    });
  },

  setSession: (session) => {
    set({
      session,
      hasActiveLiveSession: session !== null && isSessionActive(session.status),
    });
  },

  clearLiveSession: () => {
    liveGameEngine.reset();
    set({
      ...sessionInitial,
      phase: MazeGamePhase.START,
      lastEndGameTotalScore: null,
      lives: MAZE_MAX_LIVES,
    });
  },

  prepareLiveGameRouteEntry: () => {
    const { phase } = get();
    if (phase !== MazeGamePhase.WIN && phase !== MazeGamePhase.LOSE) {
      return;
    }
    set({
      phase: MazeGamePhase.START,
      score: 0,
      timer: MAZE_START_TIMER,
      level: 1,
      lastEndGameTotalScore: null,
      lives: MAZE_MAX_LIVES,
    });
  },

  applyStatusOrThrow: (data) => {
    set({
      session: data,
      hasActiveLiveSession: isSessionActive(data.status),
    });
  },

  startGame: async (difficulty) => {
    set({ isStarting: true });
    try {
      const data = liveGameEngine.startGame(difficulty);
      get().applyStatusOrThrow(data);
      set({ lastEndGameTotalScore: null });
      return data;
    } finally {
      set({ isStarting: false });
    }
  },

  fetchStatus: async () => {
    set({ isSyncing: true });
    try {
      const data = liveGameEngine.getStatus();
      if (!data) {
        set({ session: null, hasActiveLiveSession: false });
        return null;
      }
      get().applyStatusOrThrow(data);
      return data;
    } finally {
      set({ isSyncing: false });
    }
  },

  submitMoves: async () => {
    liveGameEngine.submitMoves();
  },

  advanceRoundAfterReachingEnd: async () => {
    const { nextMaze, endBoard, endGame } = liveGameEngine.advanceRoundAfterReachingEnd();
    set((state) => {
      if (!state.session) {
        return state;
      }
      const nextScoreboard =
        endGame === null
          ? state.session.scoreboard
          : { ...state.session.scoreboard, totalScore: endGame.totalScore, timeBonus: endGame.timeBonus };
      return {
        session: {
          ...state.session,
          maze: nextMaze ?? state.session.maze,
          scoreboard: nextScoreboard,
          completedBoards: (state.session.completedBoards ?? 0) + 1,
        },
        hasActiveLiveSession: !endBoard.gameOver,
        level: nextMaze ? nextMaze.roundNumber : state.level,
        ...(endGame === null ? {} : { lastEndGameTotalScore: endGame.totalScore }),
      };
    });
    return { nextMaze, endBoard, endGame };
  },

  skipRound: async () => {
    const { nextMaze, endBoard, endGame } = liveGameEngine.skipRound();
    set((state) => {
      if (!state.session) {
        return state;
      }
      const nextScoreboard =
        endGame === null
          ? state.session.scoreboard
          : { ...state.session.scoreboard, totalScore: endGame.totalScore, timeBonus: endGame.timeBonus };
      return {
        session: {
          ...state.session,
          maze: nextMaze ?? state.session.maze,
          scoreboard: nextScoreboard,
          // completedBoards intentionally left unchanged — a skip doesn't count as completed.
        },
        hasActiveLiveSession: !endBoard.gameOver,
        level: nextMaze ? nextMaze.roundNumber : state.level,
        ...(endGame === null ? {} : { lastEndGameTotalScore: endGame.totalScore }),
      };
    });
    return { nextMaze, endBoard, endGame };
  },

  endGameEarly: async () => {
    if (!get().hasActiveLiveSession) {
      return null;
    }
    const endGame = liveGameEngine.endGameEarly();
    if (!endGame) {
      set({ hasActiveLiveSession: false });
      return null;
    }
    set((state) => ({
      hasActiveLiveSession: false,
      lastEndGameTotalScore: endGame.totalScore,
      score: endGame.totalScore,
      session: state.session
        ? {
            ...state.session,
            scoreboard: {
              ...state.session.scoreboard,
              totalScore: endGame.totalScore,
              timeBonus: endGame.timeBonus,
            },
          }
        : state.session,
    }));
    return endGame;
  },
});

export const useLiveStore = create(liveStoreSlice);
